import React from 'react';
import { Box, Typography, useMediaQuery, CircularProgress, LinearProgress } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useDashboard } from '../../contexts/DashboardContext';
import { useDashboardData } from '../../hooks/useDashboardData';
import SectionContainer from '../ui/SectionContainer';
import CongestionLegend from './CongestionLegend';
import AnalysisInfoButton from '../ui/AnalysisInfoButton';
import { useTodayData } from '../../hooks/today/useTodayData';
import { getLocationDisplayName } from '../../utils/todayUtils';
import WeeklyTrend from '../today/WeeklyTrend';
import HourlyDetails from '../today/HourlyDetails';
import { detectLanguageFromPathname, translate } from '../../i18n';

const TodayDetails = () => {
    const { action: selectedAction, location: selectedLocation } = useDashboard();
    const location = useLocation();
    const language = detectLanguageFromPathname(location.pathname);
    const t = (key, params) => translate(language, key, params);
    const { loading } = useDashboardData();
    const isMobile = useMediaQuery('(max-width:768px)');
    
    const { todayData, summaryData, error, fetchLoading, getTodaysDate } = useTodayData(selectedLocation);
    
    // スクロール連動のためのRef（時間別詳細で使用）
    const handleScroll = (sourceRef, targetRefs) => {
        if (!sourceRef.current) return;
        
        const scrollLeft = sourceRef.current.scrollLeft;
        targetRefs.forEach(targetRef => {
            if (targetRef.current && targetRef !== sourceRef) {
                targetRef.current.scrollLeft = scrollLeft;
            }
        });
    };

    // "today_details"アクションが選択された時のみ表示
    if (!selectedAction || selectedAction !== 'today_details') {
        return null;
    }

    // ローディング中の表示（他のグラフと同様のホイールアニメーション）
    const isLoading = selectedAction === 'today_details' && (loading || fetchLoading);
    if (isLoading) {
        return (
            <SectionContainer>
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    minHeight: '200px',
                    flexDirection: 'column',
                    gap: 2,
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                    px: isMobile ? 1 : 2,
                    py: isMobile ? 2 : 3
                }}>
                    <CircularProgress 
                        size={48}
                        thickness={4}
                        sx={{ color: '#383947' }}
                    />
                    <Typography variant="h6" color="primary" fontWeight="bold">
                        {t('common.loadingData')}
                    </Typography>
                    <Box sx={{ width: '300px', mt: 1 }}>
                        <LinearProgress 
                            variant="indeterminate"
                            sx={{
                                height: 4,
                                borderRadius: 2,
                                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                '& .MuiLinearProgress-bar': {
                                    borderRadius: 2,
                                    backgroundColor: '#383947',
                                }
                            }}
                        />
                    </Box>
                </Box>
            </SectionContainer>
        );
    }

    // エラー表示
    if (error) {
        return (
            <SectionContainer>
                <Typography variant="body1" color="error" align="center">
                    {error}
                </Typography>
            </SectionContainer>
        );
    }

    // データがない場合
    if (!todayData || !summaryData) {
        return (
            <SectionContainer>
                <Typography variant="body1" align="center">
                    {t('common.selectLocationFirst')}
                </Typography>
            </SectionContainer>
        );
    }

    return (
        <SectionContainer>
            {/* タイトル部分 */}
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                px: 1
            }}>
                <Typography 
                    variant={isMobile ? "subtitle1" : "h5"} 
                    sx={{ textAlign: isMobile ? 'center' : 'left' }}
                >
                    {getLocationDisplayName(selectedLocation, language)}{t('todayDetails.titleSuffix')}
                </Typography>
                
                <AnalysisInfoButton 
                    analysisType="calendar"
                    place={selectedLocation}
                />
            </Box>

            <CongestionLegend
                showCalculationNote={false}
                legendType="calendar"
            />

            {summaryData && summaryData.data && (
                <>
                    {/* 週間動向の比較セクション */}
                    <Box sx={{ mb: 0.5 }}>
                   
                        {/* PC表示時は横並び、モバイルは縦並び */}
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: isMobile ? 'column' : 'row',
                            gap: isMobile ? 0.5 : 2,
                            alignItems: isMobile ? 'stretch' : 'flex-start'
                        }}>
                            {/* 今年のデータ */}
                            <Box sx={{ 
                                flex: isMobile ? 'none' : 1,
                                minWidth: 0
                            }}>
                                {!isMobile && (
                                    <Typography 
                                        variant="body1"
                                        sx={{ 
                                            fontWeight: 'bold', 
                                            mb: 1,
                                            color: 'text.primary',
                                            textAlign: 'center'
                                        }}
                                    >
                                        {t('todayDetails.thisYear')}
                                    </Typography>
                                )}
                                <WeeklyTrend 
                                    summaryData={summaryData}
                                    getTodaysDate={getTodaysDate}
                                    isHistorical={false}
                                    isCompact={!isMobile}
                                />
                            </Box>

                            {/* 去年のデータ */}
                            <Box sx={{ 
                                flex: isMobile ? 'none' : 1,
                                minWidth: 0
                            }}>
                                {!isMobile && (
                                    <Typography 
                                        variant="body1"
                                        sx={{ 
                                            fontWeight: 'bold', 
                                            color: 'text.primary',
                                            textAlign: 'center'
                                        }}
                                    >
                                        {t('todayDetails.lastYear')}
                                    </Typography>
                                )}
                                <WeeklyTrend 
                                    summaryData={summaryData}
                                    getTodaysDate={getTodaysDate}
                                    isHistorical={true}
                                    isCompact={!isMobile}
                                />
                            </Box>
                        </Box>
                    </Box>
                </>
            )}

            {/* セクション間の境界線 */}
            <Box sx={{ 
                width: '100%', 
                height: '1.5px', 
                backgroundColor: '#bbb', 
                position: 'relative',
                my: 2.3
            }}>
                
            </Box>

            {/* 時間別詳細セクション */}
            <HourlyDetails 
                todayData={todayData}
                handleScroll={handleScroll}
            />

        </SectionContainer>
    );
};

export default TodayDetails;
